(() => {
  const ROOT_SELECTOR = '[data-sp-scenes]';
  const OPEN_MODAL_SELECTOR = '[data-sp-scenes-lightbox]:not([hidden])';
  const LOCK_CLASS = 'sp-scenes-is-locked';

  const setPageLock = (locked) => {
    document.documentElement.classList.toggle(LOCK_CLASS, locked);
    document.body.classList.toggle(LOCK_CLASS, locked);
  };

  const releasePageLock = () => {
    if (!document.querySelector(OPEN_MODAL_SELECTOR)) setPageLock(false);
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.spScenesReady === 'true') return;

    const modal = gallery.querySelector('[data-sp-scenes-lightbox]');
    const modalImage = modal?.querySelector('[data-sp-scenes-modal-image]');
    const modalKeyword = modal?.querySelector('[data-sp-scenes-modal-keyword]');
    const modalTitle = modal?.querySelector('[data-sp-scenes-modal-title]');
    const modalBody = modal?.querySelector('[data-sp-scenes-modal-body]');
    const closeButton = modal?.querySelector('.sp-scenes-lightbox__close');
    const triggers = Array.from(gallery.querySelectorAll('[data-sp-scene-trigger]'));

    if (!modal || !modalImage || !modalKeyword || !modalTitle || !modalBody || !triggers.length) return;

    let returnFocus = null;

    const getFocusableElements = () =>
      Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);

    const close = ({ restoreFocus = true } = {}) => {
      if (modal.hidden) return;

      modal.hidden = true;
      modal.removeAttribute('data-sp-scenes-open');
      document.removeEventListener('keydown', handleKeydown);
      releasePageLock();

      if (restoreFocus && returnFocus?.isConnected) returnFocus.focus();
      returnFocus = null;
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const open = (trigger) => {
      const scene = trigger.closest('[data-sp-scene]');
      const sourceImage = trigger.querySelector('img');
      const caption = scene?.querySelector('[data-sp-scene-caption]');

      if (!scene || !sourceImage || !caption) return;

      document.querySelectorAll(OPEN_MODAL_SELECTOR).forEach((openModal) => {
        if (openModal !== modal) openModal.dispatchEvent(new CustomEvent('sp-scenes:close'));
      });

      returnFocus = trigger;
      modalImage.src = sourceImage.currentSrc || sourceImage.src;
      modalImage.srcset = sourceImage.srcset;
      modalImage.sizes = sourceImage.sizes || '92vw';
      modalImage.alt = sourceImage.alt;
      modalKeyword.textContent = caption.querySelector('[data-sp-scene-keyword]')?.textContent.trim() || '';
      modalTitle.textContent = caption.querySelector('[data-sp-scene-title]')?.textContent.trim() || '';
      modalBody.textContent = caption.querySelector('[data-sp-scene-body]')?.textContent.trim() || '';

      modal.hidden = false;
      modal.setAttribute('data-sp-scenes-open', 'true');
      setPageLock(true);
      document.addEventListener('keydown', handleKeydown);
      closeButton?.focus();
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', () => open(trigger)));
    modal.querySelectorAll('[data-sp-scenes-close]').forEach((control) => {
      control.addEventListener('click', () => close());
    });
    modal.addEventListener('sp-scenes:close', () => close({ restoreFocus: false }));

    gallery.spScenesCleanup = () => {
      close({ restoreFocus: false });
      delete gallery.dataset.spScenesReady;
    };
    gallery.dataset.spScenesReady = 'true';
  };

  const init = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initGallery(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(ROOT_SELECTOR)) event.target.spScenesCleanup?.();
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((gallery) => gallery.spScenesCleanup?.());
  });
})();
